import React, { useState, useEffect, useRef, useCallback } from 'react';
import { db } from '../../firebase/firebaseConfig';
import { collection, getDocs, doc, deleteDoc, getDoc, updateDoc, arrayUnion, arrayRemove, query, where } from 'firebase/firestore';
import DeckEditor from './DeckEditor';
import SpacedRepetition from '../SRS/SpacedRepetition';
import DeckCollection from './DeckCollection';
import Header from './Header';
import IconRenderer, { normalizeLanguageIconKey } from '../ui/IconRenderer';
import CookbookSnippetProgressStats from '../ui/CookbookSnippetProgressStats';
import './DeckManager.css';
import { ReactComponent as FilterSearchIcon } from '../../Icons/search.svg';
import { ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react';
import OptionsMenu from './OptionsMenu';
import MoveCookbookMenu from './MoveCookbookMenu';
import { isRoadmapUserCookbook } from '../../services/roadmapService';
import { markOnboardingCookbookDismissed } from '../../utils/onboarding';
import {
  buildCardProgressMap,
  isCookbookFullyMastered,
  loadCookbookCardProgress,
  loadCookbookPracticeStartedCards
} from '../../utils/cookbookCardProgress';

/** Normalize deck language for deduplication and filtering (case-insensitive). */
function normalizeDeckLanguageKey(lang) {
  if (!lang || typeof lang !== 'string') return 'other';
  const t = lang.trim().toLowerCase();
  if (t === 'cpp' || t === 'c++' || t.includes('cplusplus')) return 'cpp';
  if (t === 'csharp' || t === 'c#' || t.includes('c-sharp')) return 'csharp';
  return t;
}

const DECK_LANGUAGE_LABEL_BY_KEY = {
  javascript: 'JavaScript',
  typescript: 'TypeScript',
  python: 'Python',
  java: 'Java',
  cpp: 'C++',
  csharp: 'C#',
  go: 'Go',
  ruby: 'Ruby',
  php: 'PHP',
  rust: 'Rust',
  swift: 'Swift',
  kotlin: 'Kotlin',
  other: 'Other'
};

function deckLanguageDisplayLabel(key) {
  if (!key || key === 'all') return 'All Languages';
  return DECK_LANGUAGE_LABEL_BY_KEY[key] || key.charAt(0).toUpperCase() + key.slice(1);
}

function deckManagerLanguageFilterIconKey(lang) {
  if (!lang || lang === 'all') return 'code';
  const key = normalizeLanguageIconKey(lang) || normalizeDeckLanguageKey(lang);
  if (!key || key === 'other') return 'code';
  return key;
}

function DeckManagerLanguageFilterLabel({ lang, children }) {
  return (
    <span className="deck-manager-library-filter-lang-option">
      <IconRenderer
        name={deckManagerLanguageFilterIconKey(lang)}
        type="language"
        size="small"
        className="deck-manager-library-filter-lang-icon"
        aria-hidden
      />
      <span>{children}</span>
    </span>
  );
}

/** Matches Home cookbook rows (`IconRenderer` `large` at 32px + ~1.3× scale). */
const DECK_MANAGER_COOKBOOK_LANG_ICON_STYLE = { width: '35px', height: '35px' };

function inferLanguageHintFromDeckTitle(title) {
  if (!title || typeof title !== 'string') return '';
  const t = title.toLowerCase();
  if (/\bjavascript\b/.test(t) || /\bnode\.?js\b/.test(t) || /\becmascript\b/.test(t)) return 'javascript';
  if (/\btypescript\b/.test(t)) return 'typescript';
  if (/\bpython\b/.test(t)) return 'python';
  if (/\brust\b/.test(t)) return 'rust';
  if (/\bgolang\b/.test(t)) return 'go';
  if (/\bc\+\+\b|\bcpp\b/.test(t)) return 'cpp';
  if (/\bc#\b/.test(t)) return 'csharp';
  if (/\bjava\b/.test(t)) return 'java';
  return '';
}

/** Deck says `javascript` but title is clearly Java — wrong metadata from some catalogs. */
function disambiguateJavaVsJsFromTitle(title) {
  if (!title || typeof title !== 'string') return false;
  const t = title.toLowerCase();
  const jsLike = /\bjavascript\b|\bjs\b|\bnode\.?js\b|\btypescript\b|\breact\b|\bvue\b|\bangular\b/.test(t);
  if (jsLike) return false;
  return /\bjava\b/.test(t);
}

/** Same language icon resolution as Home in-progress cookbook cards. */
function deckManagerCookbookIconLanguage(deck) {
  const raw = String(
    deck?.language ?? deck?.lang ?? deck?.programmingLanguage ?? deck?.programming_language ?? ''
  ).trim();
  const title = deck?.name || deck?.title || '';
  let key = normalizeLanguageIconKey(raw);
  if (key === 'javascript' && disambiguateJavaVsJsFromTitle(title)) key = 'java';
  if (!key) {
    key = normalizeLanguageIconKey(inferLanguageHintFromDeckTitle(title));
  }
  if (!key) key = 'code';
  return key;
}

function truncateDeckManagerTitle(title, maxLength = 85) {
  if (!title) return '';
  if (title.length <= maxLength) return title;
  return `${title.substring(0, maxLength).trim()}...`;
}

const DeckManager = ({
  userId,
  onStartStudying,
  initialActiveTab,
  libraryNavNonce = 0,
  onLibraryDeckEditorOpenChange
}) => {
  const getMasteryColor = (successRate) => {
    if (successRate >= 90) return '#28a745'; // Excellent
    if (successRate >= 70) return '#17a2b8'; // Good
    if (successRate >= 50) return '#ffc107'; // Fair
    if (successRate >= 30) return '#fd7e14'; // Needs Work
    return '#dc3545'; // Poor
  };

  const [decks, setDecks] = useState([]);
  const [collections, setCollections] = useState([]);
  const [libraryCookbookCount, setLibraryCookbookCount] = useState(0);
  const [selectedDeck, setSelectedDeck] = useState(null);
  const [selectedCollection, setSelectedCollection] = useState(null);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [srs] = useState(new SpacedRepetition(userId));
  const [deckProgressById, setDeckProgressById] = useState({});
  const [practiceLoadingDeckId, setPracticeLoadingDeckId] = useState(null);
  const deckProgressMapRef = useRef(new Map());
  const [activeTab, setActiveTab] = useState('cookbooks');
  const [showMoveToCollection, setShowMoveToCollection] = useState(false);
  const [sortFilter, setSortFilter] = useState('recent');
  const [languageFilter, setLanguageFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showSortOptions, setShowSortOptions] = useState(false);
  const [showLanguageOptions, setShowLanguageOptions] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState(null);
  const [availableLanguages, setAvailableLanguages] = useState(['all']);
  const [cookbookTagFilter, setCookbookTagFilter] = useState('all');
  const [showCookbookTagOptions, setShowCookbookTagOptions] = useState(false);
  const [availableCookbookTags, setAvailableCookbookTags] = useState(['all']);
  const [collectionTagFilter, setCollectionTagFilter] = useState('all');
  const [showCollectionTagOptions, setShowCollectionTagOptions] = useState(false);
  const [availableCollectionTags, setAvailableCollectionTags] = useState(['all']);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(6);
  const [moveMenuOpen, setMoveMenuOpen] = useState(false);
  const [cookbookToMove, setCookbookToMove] = useState(null);
  const [filterSearchOpen, setFilterSearchOpen] = useState(false);
  const filterSearchInputRef = useRef(null);

  useEffect(() => {
    if (searchQuery.trim()) {
      setFilterSearchOpen(true);
    }
  }, [searchQuery]);

  const loadLibraryCookbookCount = async () => {
    try {
      const decksRef = collection(db, `users/${userId}/decks`);
      const snapshot = await getDocs(decksRef);
      const count = snapshot.docs
        .map((deckDoc) => ({ id: deckDoc.id, ...deckDoc.data() }))
        .filter((deck) => deck.type === 'cookbook' || !deck.type)
        .filter((deck) => !isRoadmapUserCookbook(deck))
        .length;
      setLibraryCookbookCount(count);
    } catch (error) {
      console.error('Error loading library cookbook count:', error);
    }
  };

  useEffect(() => {
    loadCollections();
    loadLibraryCookbookCount();
    if (activeTab !== 'collections' || selectedCollection) {
      loadDecks();
    }
  }, [userId, activeTab, sortFilter, languageFilter, cookbookTagFilter, collectionTagFilter, selectedCollection, searchQuery]);

  useEffect(() => {
    if (libraryNavNonce > 0) {
      setSelectedDeck(null);
    }
  }, [libraryNavNonce]);

  useEffect(() => {
    onLibraryDeckEditorOpenChange?.(Boolean(selectedDeck));
  }, [selectedDeck, onLibraryDeckEditorOpenChange]);

  useEffect(() => {
    return () => {
      onLibraryDeckEditorOpenChange?.(false);
    };
  }, [onLibraryDeckEditorOpenChange]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.filter-dropdown')) {
        setShowSortOptions(false);
        setShowLanguageOptions(false);
        setShowCookbookTagOptions(false);
        setShowCollectionTagOptions(false);
      }
      if (!event.target.closest('.deck-dropdown')) {
        setOpenDropdown(null);
        setShowMoveToCollection(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadCollections = async () => {
    try {
      const collectionsRef = collection(db, `users/${userId}/collections`);
      const snapshot = await getDocs(collectionsRef);
      const loadedCollections = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        deckCount: (doc.data().deckIds || []).length
      }));
      setCollections(loadedCollections);

      // Get unique collection tags
      const collectionTags = new Set(loadedCollections.flatMap(collection => collection.tags || []));
      setAvailableCollectionTags(['all', ...Array.from(collectionTags)]);
    } catch (error) {
      console.error('Error loading collections:', error);
    }
  };

  const handleCollectionSelect = async (collectionId) => {
    setSelectedCollection(collectionId);
    setActiveTab('cookbooks');
    await loadDecks();
  };

  const handleCollectionDelete = async (collectionId) => {
    try {
      const collectionRef = doc(db, `users/${userId}/collections/${collectionId}`);
      const collectionDoc = await getDoc(collectionRef);

      if (collectionDoc.exists()) {
        const deckIds = collectionDoc.data().deckIds || [];

        for (const deckId of deckIds) {
          const deckRef = doc(db, `users/${userId}/decks/${deckId}`);
          await updateDoc(deckRef, {
            collectionId: null
          });
        }
      }

      await deleteDoc(collectionRef);
      loadCollections();
      if (selectedCollection === collectionId) {
        setSelectedCollection(null);
      }
    } catch (error) {
      console.error('Error deleting collection:', error);
    }
  };

  const handleCollectionRename = async (collectionId, newName) => {
    try {
      const collectionRef = doc(db, `users/${userId}/collections/${collectionId}`);
      await updateDoc(collectionRef, {
        name: newName
      });
      loadCollections();
    } catch (error) {
      console.error('Error renaming collection:', error);
    }
  };

  const handleMoveToCollection = async (cookbook, collection) => {
    try {
      const deckRef = doc(db, `users/${userId}/decks/${cookbook.id}`);
      const deckDoc = await getDoc(deckRef);

      if (!deckDoc.exists()) {
        console.error('Deck not found');
        return;
      }

      const oldCollectionId = deckDoc.data().collectionId;
      if (oldCollectionId) {
        const oldCollectionRef = doc(db, `users/${userId}/collections/${oldCollectionId}`);
        await updateDoc(oldCollectionRef, {
          deckIds: arrayRemove(cookbook.id)
        });
      }

      const newCollectionRef = doc(db, `users/${userId}/collections/${collection.id}`);
      await updateDoc(newCollectionRef, {
        deckIds: arrayUnion(cookbook.id)
      });

      await updateDoc(deckRef, {
        collectionId: collection.id
      });

      loadDecks();
      loadCollections();

      setMoveMenuOpen(false);
      setOpenDropdown(null);
    } catch (error) {
      console.error('Error moving deck to collection:', error);
    }
  };

  const removeDeckFromCollection = async (deckId) => {
    try {
      const collectionRef = doc(db, `users/${userId}/collections/${selectedCollection}`);
      await updateDoc(collectionRef, {
        deckIds: arrayRemove(deckId)
      });

      const deckRef = doc(db, `users/${userId}/decks/${deckId}`);
      await updateDoc(deckRef, {
        collectionId: null
      });

      loadDecks();
      loadCollections();

      setOpenDropdown(null);
    } catch (error) {
      console.error('Error removing deck from collection:', error);
    }
  };

  const handlePracticeStartedCookbook = useCallback(
    async (e, deck) => {
      e.stopPropagation();
      const deckId = deck?.id;
      const progress = deckProgressById[deckId];
      if (!deckId || !userId || !onStartStudying || practiceLoadingDeckId) return;
      if (!progress?.started) return;

      setPracticeLoadingDeckId(deckId);
      try {
        const progressMap =
          deckProgressMapRef.current instanceof Map && deckProgressMapRef.current.size > 0
            ? deckProgressMapRef.current
            : buildCardProgressMap(await srs.fetchAllCardProgressRows());
        const cards = await loadCookbookPracticeStartedCards(userId, deckId, progressMap);
        if (cards.length === 0) return;

        onStartStudying({
          practiceStartedSession: true,
          deckId,
          cards,
          startCardId: cards[0].id
        });
      } catch (err) {
        console.error('DeckManager: failed to start cookbook practice session', err);
      } finally {
        setPracticeLoadingDeckId(null);
      }
    },
    [userId, onStartStudying, practiceLoadingDeckId, deckProgressById, srs]
  );

  const loadDecks = async () => {
    try {
      const decksRef = collection(db, `users/${userId}/decks`);
      const [snapshot, progressRows] = await Promise.all([
        getDocs(decksRef),
        srs.fetchAllCardProgressRows()
      ]);
      const progressMap = buildCardProgressMap(progressRows);
      deckProgressMapRef.current = progressMap;
      let loadedDecks = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      if (!selectedCollection) {
        const cookbookCount = loadedDecks
          .filter((deck) => deck.type === 'cookbook' || !deck.type)
          .filter((deck) => !isRoadmapUserCookbook(deck))
          .length;
        setLibraryCookbookCount(cookbookCount);
      }

      if (selectedCollection) {
        const collectionRef = doc(db, `users/${userId}/collections/${selectedCollection}`);
        const collectionDoc = await getDoc(collectionRef);
        if (collectionDoc.exists()) {
          const deckIds = collectionDoc.data().deckIds || [];
          loadedDecks = loadedDecks.filter(deck => deckIds.includes(deck.id));
        }
      } else {
        loadedDecks = loadedDecks.filter(deck => {
          if (activeTab === 'cookbooks') {
            return deck.type === 'cookbook' || !deck.type;
          }
          return true;
        });
      }

      if (activeTab === 'cookbooks') {
        loadedDecks = loadedDecks.filter((deck) => !isRoadmapUserCookbook(deck));
      }

      // Unique languages by normalized key (avoids Java vs java vs JAVA duplicates)
      const languageKeys = new Set(
        loadedDecks.map((deck) => normalizeDeckLanguageKey(deck.language))
      );
      const sortedLanguageKeys = Array.from(languageKeys).sort((a, b) =>
        deckLanguageDisplayLabel(a).localeCompare(deckLanguageDisplayLabel(b), undefined, {
          sensitivity: 'base'
        })
      );
      setAvailableLanguages(['all', ...sortedLanguageKeys]);

      setLanguageFilter((prev) => {
        if (prev === 'all') return prev;
        const k = normalizeDeckLanguageKey(prev);
        if (!sortedLanguageKeys.includes(k)) return 'all';
        return k;
      });

      if (activeTab === 'cookbooks') {
        const cookbookTags = new Set(loadedDecks.flatMap(deck => deck.tags || []));
        setAvailableCookbookTags(['all', ...Array.from(cookbookTags)]);

        if (languageFilter !== 'all') {
          const filterKey = normalizeDeckLanguageKey(languageFilter);
          loadedDecks = loadedDecks.filter(
            (deck) => normalizeDeckLanguageKey(deck.language) === filterKey
          );
        }

        if (cookbookTagFilter !== 'all') {
          loadedDecks = loadedDecks.filter(deck => deck.tags && deck.tags.includes(cookbookTagFilter));
        }
      }

      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        loadedDecks = loadedDecks.filter(deck =>
          deck.name.toLowerCase().includes(query) ||
          (deck.description && deck.description.toLowerCase().includes(query))
        );
      }

      const progressEntries = await Promise.all(
        loadedDecks.map(async (deck) => {
          try {
            const progress = await loadCookbookCardProgress(userId, deck.id, progressMap);
            return [deck.id, progress];
          } catch (error) {
            console.error('Error loading deck progress:', deck.id, error);
            return [
              deck.id,
              { total: 0, started: 0, startedNotDue: 0, mastered: 0, averageSuccessRate: 0 }
            ];
          }
        })
      );
      const progressByDeckId = Object.fromEntries(progressEntries);

      setDeckProgressById((prev) => {
        const next = { ...prev };
        for (const [deckId, progress] of progressEntries) {
          next[deckId] = {
            total: progress.total,
            started: progress.started,
            startedNotDue: progress.startedNotDue,
            mastered: progress.mastered
          };
        }
        return next;
      });

      if (sortFilter === 'mastered') {
        loadedDecks = loadedDecks.filter((deck) =>
          isCookbookFullyMastered(progressByDeckId[deck.id])
        );
      }

      loadedDecks.sort((a, b) => {
        switch (sortFilter) {
          case 'recent':
          case 'mastered':
            return new Date(b.lastAccessed || b.createdAt) - new Date(a.lastAccessed || a.createdAt);
          case 'created':
            return new Date(b.createdAt) - new Date(a.createdAt);
          default:
            return 0;
        }
      });

      setDecks(loadedDecks);
    } catch (error) {
      console.error('Error loading decks:', error);
    }
  };

  const deleteDeck = async (deckId) => {
    if (!window.confirm('Are you sure you want to delete this deck?')) return;

    try {
      // Cookbook deletion: remove all snippets (cards), then all SRS review data for this deck
      const cardsRef = collection(db, `users/${userId}/decks/${deckId}/cards`);
      const cardsSnapshot = await getDocs(cardsRef);

      for (const cardDoc of cardsSnapshot.docs) {
        // Delete card progress
        const progressRef = doc(db, `users/${userId}/progress/${deckId}_${cardDoc.id}`);
        try {
          await deleteDoc(progressRef);
        } catch (error) {
          console.error('Error deleting card progress:', error);
          // Continue with deletion even if progress deletion fails
        }

        // Delete the card itself
        try {
          await deleteDoc(doc(db, `users/${userId}/decks/${deckId}/cards/${cardDoc.id}`));
        } catch (error) {
          console.error('Error deleting card:', error);
          // Continue with deletion even if card deletion fails
        }
      }

      // Get the deck document to check if it's in a collection
      const deckRef = doc(db, `users/${userId}/decks/${deckId}`);
      const deckDoc = await getDoc(deckRef);
      const isOnboardingCookbook = deckDoc.exists() && deckDoc.data()?.onboardingCookbook === true;

      // If the deck exists and is in a collection, try to remove it from the collection
      if (deckDoc.exists() && deckDoc.data().collectionId) {
        const collectionId = deckDoc.data().collectionId;
        const collectionRef = doc(db, `users/${userId}/collections/${collectionId}`);

        // Check if collection exists before updating
        const collectionDoc = await getDoc(collectionRef);
        if (collectionDoc.exists()) {
          try {
            await updateDoc(collectionRef, {
              deckIds: arrayRemove(deckId)
            });
          } catch (error) {
            console.error('Error removing deck from collection:', error);
            // Continue with deletion even if collection update fails
          }
        }
      }

      // Delete any SRS dashboard entries for this deck
      try {
        // Query for any progress entries that might reference this deck
        const progressRef = collection(db, `users/${userId}/progress`);
        const q = query(progressRef, where('deckId', '==', deckId));
        const progressSnapshot = await getDocs(q);

        // Delete each progress entry
        for (const progressDoc of progressSnapshot.docs) {
          await deleteDoc(doc(db, `users/${userId}/progress/${progressDoc.id}`));
        }
      } catch (error) {
        console.error('Error deleting deck progress from SRS dashboard:', error);
        // Continue with deletion even if SRS dashboard cleanup fails
      }

      // Finally delete the deck itself
      await deleteDoc(deckRef);

      if (isOnboardingCookbook) {
        await markOnboardingCookbookDismissed(userId);
      }

      // Update UI
      loadDecks();
      loadCollections();
      setOpenDropdown(null);

      if (selectedDeck === deckId) {
        setSelectedDeck(null);
      }
    } catch (error) {
      console.error('Error deleting deck:', error);
      window.alert('Error deleting deck. Please try again.');
    }
  };

  if (selectedDeck) {
    return (
      <DeckEditor
        userId={userId}
        deckId={selectedDeck}
      />
    );
  }


  const selectedCollectionName = selectedCollection
    ? collections.find(c => c.id === selectedCollection)?.name || ''
    : '';

  return (
    <div className="deck-manager">
      <div className="deck-manager-library-chrome">
        <Header
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          setSelectedCollection={setSelectedCollection}
          cookbookCount={libraryCookbookCount}
          collectionCount={collections.length}
        />

        <div className="filters-section">
          {selectedCollection && (
            <div className="collection-title">
              {selectedCollectionName}
            </div>
          )}
          <div className="filter-dropdown">
            <button
              type="button"
              className="filter-button"
              onClick={() => {
                setShowLanguageOptions(false);
                setShowCookbookTagOptions(false);
                setShowCollectionTagOptions(false);
                setShowSortOptions(!showSortOptions);
              }}
            >
              {sortFilter.charAt(0).toUpperCase() + sortFilter.slice(1)}
              <ChevronDown className="filter-button__chevron" size={12} strokeWidth={2} aria-hidden />
            </button>
            {showSortOptions && (
              <div className="filter-options">
                <div className="filter-option" onClick={() => {
                  setSortFilter('recent');
                  setShowSortOptions(false);
                }}>Recent</div>
                <div className="filter-option" onClick={() => {
                  setSortFilter('created');
                  setShowSortOptions(false);
                }}>Created</div>
                <div className="filter-option" onClick={() => {
                  setSortFilter('mastered');
                  setShowSortOptions(false);
                }}>Mastered</div>
              </div>
            )}
          </div>

          {activeTab === 'collections' && !selectedCollection ? (
            <div className="filter-dropdown">
              <button
                type="button"
                className="filter-button"
                onClick={() => {
                  setShowSortOptions(false);
                  setShowLanguageOptions(false);
                  setShowCookbookTagOptions(false);
                  setShowCollectionTagOptions(!showCollectionTagOptions);
                }}
              >
                {collectionTagFilter === 'all' ? 'All Tags' : collectionTagFilter}
                <ChevronDown className="filter-button__chevron" size={12} strokeWidth={2} aria-hidden />
              </button>
              {showCollectionTagOptions && (
                <div className="filter-options">
                  {availableCollectionTags.map(tag => (
                    <div
                      key={tag}
                      className="filter-option"
                      onClick={() => {
                        setCollectionTagFilter(tag);
                        setShowCollectionTagOptions(false);
                      }}
                    >
                      {tag === 'all' ? 'All Tags' : tag}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : activeTab === 'cookbooks' && (
            <div className="filter-dropdown">
              <button
                type="button"
                className="filter-button"
                onClick={() => {
                  setShowSortOptions(false);
                  setShowLanguageOptions(false);
                  setShowCookbookTagOptions(!showCookbookTagOptions);
                  setShowCollectionTagOptions(false);
                }}
              >
                {cookbookTagFilter === 'all' ? 'All Tags' : cookbookTagFilter}
                <ChevronDown className="filter-button__chevron" size={12} strokeWidth={2} aria-hidden />
              </button>
              {showCookbookTagOptions && (
                <div className="filter-options">
                  {availableCookbookTags.map(tag => (
                    <div
                      key={tag}
                      className="filter-option"
                      onClick={() => {
                        setCookbookTagFilter(tag);
                        setShowCookbookTagOptions(false);
                      }}
                    >
                      {tag === 'all' ? 'All Tags' : tag}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="filter-dropdown">
            <button
              type="button"
              className="filter-button"
              onClick={() => {
                setShowSortOptions(false);
                setShowCookbookTagOptions(false);
                setShowCollectionTagOptions(false);
                setShowLanguageOptions(!showLanguageOptions);
              }}
            >
              {languageFilter === 'all' ? (
                deckLanguageDisplayLabel(languageFilter)
              ) : (
                <DeckManagerLanguageFilterLabel lang={languageFilter}>
                  {deckLanguageDisplayLabel(languageFilter)}
                </DeckManagerLanguageFilterLabel>
              )}
              <ChevronDown className="filter-button__chevron" size={12} strokeWidth={2} aria-hidden />
            </button>
            {showLanguageOptions && (
              <div className="filter-options">
                {availableLanguages.map(lang => (
                  <div
                    key={lang}
                    className="filter-option"
                    onClick={() => {
                      setLanguageFilter(lang);
                      setShowLanguageOptions(false);
                    }}
                  >
                    <DeckManagerLanguageFilterLabel lang={lang}>
                      {deckLanguageDisplayLabel(lang)}
                    </DeckManagerLanguageFilterLabel>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="filters-section__spacer" aria-hidden />

          <div
            className={`filters-section__search-wrap ${filterSearchOpen || searchQuery ? 'filters-section__search-wrap--open' : ''}`}
          >
            <div
              className="filters-section__search-inner"
              role="search"
              tabIndex={filterSearchOpen ? -1 : 0}
              onClick={() => {
                if (!filterSearchOpen) {
                  setFilterSearchOpen(true);
                  window.setTimeout(() => filterSearchInputRef.current?.focus(), 220);
                }
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  if (!filterSearchOpen) {
                    e.preventDefault();
                    setFilterSearchOpen(true);
                    window.setTimeout(() => filterSearchInputRef.current?.focus(), 220);
                  }
                }
              }}
            >
              <FilterSearchIcon className="filters-section__search-icon" aria-hidden />
              <input
                ref={filterSearchInputRef}
                type="search"
                className="filters-section__search-input"
                placeholder={
                  activeTab === 'collections' && !selectedCollection
                    ? 'Search collections...'
                    : 'Search cookbooks...'
                }
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onFocus={() => setFilterSearchOpen(true)}
                onBlur={() => {
                  if (!searchQuery.trim()) {
                    setFilterSearchOpen(false);
                  }
                }}
                aria-label="Search library"
              />
            </div>
          </div>
        </div>
      </div>

      {activeTab === 'collections' && !selectedCollection ? (
        <>
          <div className="deck-manager__cookbook-visual-scale">
            <div className="collections-list">
              {collections
                .slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
                .filter(collection => {
                  // Filter by tag
                  const matchesTag = collectionTagFilter === 'all' ||
                    (collection.tags && collection.tags.includes(collectionTagFilter));

                  // Filter by search query
                  const matchesSearch = !searchQuery ||
                    collection.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                    (collection.description && collection.description.toLowerCase().includes(searchQuery.toLowerCase()));

                  return matchesTag && matchesSearch;
                })
                .map(collection => (
                  <DeckCollection
                    key={collection.id}
                    userId={userId}
                    collection={collection}
                    onSelect={handleCollectionSelect}
                    onDelete={handleCollectionDelete}
                    onRename={handleCollectionRename}
                    tagFilter={collectionTagFilter}
                  />
                ))}
            </div>
          </div>
          <div className="deck-manager__pagination">
            <button
              type="button"
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className="deck-manager__pagination-button"
              aria-label="Previous page"
            >
              <ChevronLeft className="deck-manager__pagination-button-icon" size={16} strokeWidth={2} aria-hidden />
            </button>
            <span className="deck-manager__pagination-info">
              Page {currentPage} of {Math.ceil(collections.length / itemsPerPage) || 1}
            </span>
            <button
              type="button"
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, Math.ceil(collections.length / itemsPerPage) || 1))}
              disabled={currentPage === (Math.ceil(collections.length / itemsPerPage) || 1)}
              className="deck-manager__pagination-button"
              aria-label="Next page"
            >
              <ChevronRight className="deck-manager__pagination-button-icon" size={16} strokeWidth={2} aria-hidden />
            </button>
          </div>
        </>
      ) : (
        <>
          <div className="deck-manager__cookbook-visual-scale">
            <div className={`deck-manager__deck-list ${openDropdown ? 'deck-manager__deck-list--has-open-dropdown' : ''}`}>
              {decks
                .slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
                .map(deck => (
                  <div
                    key={deck.id}
                    className={`deck-manager-cookbook-card deck-manager-deck-item ${openDropdown === deck.id ? 'active' : ''}`}
                    onClick={(e) => {
                      if (!e.target.closest('.deck-dropdown')) {
                        onStartStudying(deck.id);
                      }
                    }}
                  >
                    <div className="deck-manager-cookbook-card__main">
                      <div className="deck-manager-cookbook-card__leading">
                        <IconRenderer
                          name={deckManagerCookbookIconLanguage(deck)}
                          type="language"
                          size="large"
                          className="deck-manager-cookbook-card__language-icon"
                          style={DECK_MANAGER_COOKBOOK_LANG_ICON_STYLE}
                          aria-hidden
                        />
                        <div className="deck-manager-cookbook-card__title-block">
                          <h3 className="deck-manager-cookbook-card__title">
                            {truncateDeckManagerTitle(deck.name)}
                          </h3>
                          <CookbookSnippetProgressStats
                            className="deck-manager-cookbook-card__progress"
                            started={deckProgressById[deck.id]?.started}
                            mastered={deckProgressById[deck.id]?.mastered}
                            total={deckProgressById[deck.id]?.total}
                            loading={deckProgressById[deck.id] === undefined}
                            cookbookTitle={deck.name}
                            onStartedClick={(e) => handlePracticeStartedCookbook(e, deck)}
                            startedDisabled={
                              practiceLoadingDeckId === deck.id ||
                              !deckProgressById[deck.id]?.started
                            }
                          />
                        </div>
                      </div>
                    </div>

                    <div className="deck-dropdown">
                      {selectedCollection ? (
                        <OptionsMenu
                          options={[
                            {
                              label: "Manage Snippets",
                              onClick: (e) => {
                                e.stopPropagation();
                                setSelectedDeck(deck.id);
                              }
                            },
                            {
                              label: "Remove from Collection",
                              onClick: (e) => {
                                e.stopPropagation();
                                removeDeckFromCollection(deck.id);
                              },
                              className: "delete"
                            },
                            {
                              label: "Delete",
                              onClick: (e) => {
                                e.stopPropagation();
                                deleteDeck(deck.id);
                              },
                              className: "delete"
                            }
                          ]}
                        />
                      ) : (
                        <OptionsMenu
                          options={[
                            {
                              label: "Manage Snippets",
                              onClick: (e) => {
                                e.stopPropagation();
                                setSelectedDeck(deck.id);
                              }
                            },
                            {
                              label: "Move to Collection",
                              onClick: (e) => {
                                e.stopPropagation();
                                setCookbookToMove(deck);
                                setMoveMenuOpen(true);
                              }
                            },
                            {
                              label: "Delete",
                              onClick: (e) => {
                                e.stopPropagation();
                                deleteDeck(deck.id);
                              },
                              className: "delete"
                            }
                          ]}
                        />
                      )}
                    </div>
                  </div>
                ))}
            </div>
            <div className="deck-manager__pagination">
              <button
                type="button"
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="deck-manager__pagination-button"
                aria-label="Previous page"
              >
                <ChevronLeft className="deck-manager__pagination-button-icon" size={16} strokeWidth={2} aria-hidden />
              </button>
              <span className="deck-manager__pagination-info">
                Page {currentPage} of {Math.max(1, Math.ceil(decks.length / itemsPerPage))}
              </span>
              <button
                type="button"
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, Math.max(1, Math.ceil(decks.length / itemsPerPage))))}
                disabled={currentPage === Math.max(1, Math.ceil(decks.length / itemsPerPage))}
                className="deck-manager__pagination-button"
                aria-label="Next page"
              >
                <ChevronRight className="deck-manager__pagination-button-icon" size={16} strokeWidth={2} aria-hidden />
              </button>
            </div>
          </div>
        </>
      )}

      <MoveCookbookMenu
        open={moveMenuOpen}
        onClose={() => {
          setMoveMenuOpen(false);
          setCookbookToMove(null);
        }}
        cookbook={cookbookToMove}
        collections={collections}
        onMoveToCollection={handleMoveToCollection}
      />
    </div>
  );
};

export default DeckManager;
